import type { Book } from '@booknest/shared';

import { genreMeta } from '@/features/discover/genre-meta.js';
import {
  Bookcase,
  type BookcaseUnitData,
} from '@/features/library/components/bookcase/Bookcase.js';

import type { UpcomingItem } from '../types.js';

interface UpcomingBookcaseProps {
  readonly entries: ReadonlyArray<UpcomingItem>;
  readonly openingBookId?: string | null;
  readonly onBookClick?: (book: Book) => void;
}

/**
 * "Rayon Bientôt" — bookcase visuel listant la wishlist à venir,
 * regroupée par genre dominant (max 2 unités).
 */
export function UpcomingBookcase({
  entries,
  openingBookId,
  onBookClick,
}: UpcomingBookcaseProps): JSX.Element {
  const books = entries.map((e) => e.book);

  if (books.length === 0) {
    return (
      <p className="rounded-2xl bg-paper p-4 text-sm text-brown-soft">
        Aucune sortie en attente — ajoute des livres à ta wishlist.
      </p>
    );
  }

  // Regroupe par genre, garde les 2 plus représentés en unités séparées,
  // le reste dans une unité "Autres".
  const byGenre = new Map<string, Book[]>();
  for (const b of books) {
    const list = byGenre.get(b.genre) ?? [];
    list.push(b);
    byGenre.set(b.genre, list);
  }
  const sorted = Array.from(byGenre.entries()).sort(
    (a, b) => b[1].length - a[1].length,
  );
  const top = sorted.slice(0, 2);
  const rest = sorted.slice(2).flatMap(([, list]) => list);

  const units: BookcaseUnitData[] = top.map(([genre, list]) => {
    const meta = genreMeta(genre as Parameters<typeof genreMeta>[0]);
    return {
      key: genre,
      label: `Rayon ${meta.label}`,
      emoji: meta.emoji,
      books: list,
      firstAsCover: true,
    };
  });
  if (rest.length > 0) {
    units.push({
      key: 'other',
      label: 'Autres wishlist',
      emoji: '💌',
      books: rest,
    });
  }

  return (
    <Bookcase
      units={units}
      maxCols={2}
      openingBookId={openingBookId ?? null}
      onBookClick={onBookClick}
    />
  );
}
